import { useEffect, useState } from "react";
import MuiCheckbox from "./MuiCheckbox";
import CartContainer from "./CartContainer";
import { DeleteOutlined } from "@ant-design/icons";
import { useCart } from "../../contexts/CartContext";

export interface ProductFull {
    id: number;
    product_category_id: number;
    cart_item_id?: number;
    code: string;
    name: string;
    link: string;
    image: string;

    pack: string;
    price: string;
    originalPrice: string;
    rawPrice: number;

    qty: number;
    max: number;

    checked: boolean;
    important: boolean;

    discount?: number;
    badges?: string[];
}

export interface StoreFull {
    id: number;
    name: string;
    link: string;
    logo: string;

    voucherText: string;

    checked: boolean;
    products: ProductFull[];
}

export type Store = StoreFull;

interface CartActionProps {
    onSelectionChange: (count: number, total: number, selectedItems: ProductFull[]) => void;
}

export default function CartAction({ onSelectionChange }: CartActionProps) {
    const { cartItems, addItem, confirmRemoveItem, confirmRemoveMultipleItems } = useCart();
    const [stores, setStores] = useState<StoreFull[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    useEffect(() => {
        if (cartItems) {
            // Create a lookup map for current checked status to persist it across refetches
            // Since API might not save checked status (no API call on toggle), we must preserve local state
            const checkedMap = new Map<number, boolean>();
            stores.forEach(s => {
                s.products.forEach(p => {
                    checkedMap.set(p.id, p.checked);
                });
            });
            // Group items by dummy store since API doesn't provide store info yet
            const mappedProducts: ProductFull[] = cartItems.map(item => {
                if (!item.product) {
                    console.warn(`Cart item with id ${item.id} has no product data, skipping.`);
                    return {
                        id: item.product_id,
                        product_category_id: 0,
                        cart_item_id: item.id,
                        code: "",
                        name: "Unknown Product",
                        link: "#",
                        image: "",
                        pack: "Đơn vị tính",
                        price: "0",
                        originalPrice: "0",
                        rawPrice: 0,
                        qty: Number(item.quantity),
                        max: 1000,
                        checked: false,
                        important: false,
                        discount: 0,
                        badges: []
                    };
                }
                const originalPrice = Number(item.product.price) || 0;
                const rawPrice = Number(item.product.discount_price ?? item.product.price) || 0;

                // Lax check for is_check from API
                let isChecked = item.is_check === 1 || String(item.is_check) === "1";

                // If we have a local state for this item, and we are just refreshing data (e.g. qty change),
                // we should trust local state over API if API doesn't persist check status.
                // However, initial load (empty stores) should respect API.
                if (checkedMap.has(item.product.id)) {
                    isChecked = checkedMap.get(item.product.id)!;
                }

                return {
                    id: item.product.id,
                    product_category_id: item.product.product_category_id,
                    cart_item_id: item.id,
                    code: item.product.slug,
                    name: item.product.name,
                    link: `/product/${item.product.slug}`,
                    image: item.product.avatar,
                    pack: item.product.packaging_spec || "Đơn vị tính", // Default
                    price: item.product?.discount_price?.toLocaleString('vi-VN') || originalPrice.toLocaleString('vi-VN'),
                    originalPrice: originalPrice.toLocaleString('vi-VN'),
                    rawPrice: rawPrice,
                    qty: Number(item.quantity),
                    max: 1000,
                    checked: isChecked,
                    important: false, // Not in API
                    discount: item.product.discount,
                    badges: []
                };
            });

            if (mappedProducts.length > 0) {
                setStores([{
                    id: 1,
                    name: "Giỏ hàng của bạn",
                    link: "#",
                    logo: "",
                    voucherText: "",
                    checked: false,
                    products: mappedProducts
                }]);
            } else {
                setStores([]);
            }
        }
    }, [cartItems]);

    // Notify parent about selection changes
    useEffect(() => {
        let totalCount = 0;
        let totalPrice = 0;
        let selectedItems: ProductFull[] = [];

        stores.forEach(store => {
            store.products.forEach(product => {
                if (product.checked) {
                    totalCount += product.qty;
                    totalPrice += product.qty * product.rawPrice;
                    selectedItems.push(product);
                }
            });
        });

        // Ensure we don't trigger unnecessary updates if values haven't changed
        // But since we can't easily check prev value here without ref, we just call it.
        // Parent should handle deduping if needed, or React state update bails out if value same.
        onSelectionChange(totalCount, totalPrice, selectedItems);
    }, [stores]); // Rely on stores updates to trigger recalculation

    // tổng sản phẩm
    const allProducts = stores.flatMap(s => s.products);
    const total = allProducts.length;
    const selected = allProducts.filter(p => p.checked).length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    useEffect(() => {
        setCurrentPage(prev => Math.min(prev, totalPages));
    }, [totalPages]);

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    let cursor = 0;
    const pagedStores = stores
        .map(store => {
            const storeStart = Math.max(0, startIndex - cursor);
            const storeEnd = Math.min(store.products.length, endIndex - cursor);
            cursor += store.products.length;

            if (storeEnd <= storeStart) {
                return null;
            }

            return {
                ...store,
                products: store.products.slice(storeStart, storeEnd)
            };
        })
        .filter((store): store is StoreFull => Boolean(store));

    const visiblePageButtons = 5;
    const firstPage = Math.max(1, currentPage - Math.floor(visiblePageButtons / 2));
    const lastPage = Math.min(totalPages, firstPage + visiblePageButtons - 1);
    const pageNumbers = Array.from({ length: lastPage - firstPage + 1 }, (_, i) => firstPage + i);

    const allChecked = selected === total && total > 0;
    const indeterminate = selected > 0 && selected < total;

    // toggle tất cả
    const toggleAll = () => {
        setStores(prev =>
            prev.map(store => ({
                ...store,
                checked: !allChecked,
                products: store.products.map(p => ({
                    ...p,
                    checked: !allChecked
                }))
            }))
        );
    };

    // toggle shop
    const toggleStore = (storeId: number) => {
        setStores(prev =>
            prev.map(store =>
                store.id === storeId
                    ? {
                        ...store,
                        checked: !store.checked,
                        products: store.products.map(p => ({
                            ...p,
                            checked: !store.checked
                        }))
                    }
                    : store
            )
        );
    };

    // toggle product
    const toggleProduct = (storeId: number, productId: number) => {
        setStores(prev =>
            prev.map(store =>
                store.id === storeId
                    ? {
                        ...store,
                        products: store.products.map(p =>
                            p.id === productId
                                ? { ...p, checked: !p.checked }
                                : p
                        ),
                        checked: store.products.every(p =>
                            p.id === productId ? !p.checked : p.checked
                        )
                            ? false
                            : store.checked
                    }
                    : store
            )
        );
    };

    // remove product
    const removeProduct = (storeId: number, productId: number) => {
        const store = stores.find(s => s.id === storeId);
        if (!store) return;
        const product = store.products.find(p => p.id === productId);
        if (!product || !product.cart_item_id) return;

        const cartItemId = product.cart_item_id.toString();
        confirmRemoveItem(cartItemId, product.name);
    };

    // toggle important
    const toggleImportant = (storeId: number, productId: number) => {
        setStores(prev =>
            prev.map(store =>
                store.id === storeId
                    ? {
                        ...store,
                        products: store.products.map(p =>
                            p.id === productId
                                ? { ...p, important: !p.important }
                                : p
                        )
                    }
                    : store
            )
        );
    };

    return (
        <div className="styles_cartContainer__uJI18">

            {/* Top Action bar giống hình */}
            <div className="styles_cartAction__yOdw7">
                <p className="styles_cartActionText__n8puB">
                    {selected}/{total} sản phẩm đã chọn
                </p>

                <div className="styles_cartActionRight__OVpce">
                    <div role="button" className="styles_cartRemoveAll__gUrix styles_outline__E_cox">
                        <div className="styles_cartRemoveIcon__q4_Ak">
                            <DeleteOutlined style={{ color: "red", cursor: "pointer" }} />
                        </div>
                        <p className="styles_cartRemoveText__beh15 hidden-1199" onClick={async () => {
                            const ids = stores.flatMap(store => store.products.filter(p => p.checked && p.cart_item_id).map(p => p.cart_item_id!.toString()));
                            if (ids.length === 0) return;
                            confirmRemoveMultipleItems(ids);
                        }}>Xoá khỏi giỏ hàng</p>
                    </div>

                    <label
                        className="styles_cartSelectAll__3_iRm MuiFormControlLabel-labelPlacementStart"
                        onClick={toggleAll}
                    >
                        <MuiCheckbox
                            checked={allChecked}
                            indeterminate={indeterminate}
                            onChange={toggleAll}
                        />
                        <span>Chọn tất cả</span>
                    </label>

                </div>
            </div>

            {/* List store + products */}
            <CartContainer
                stores={pagedStores}
                toggleStore={toggleStore}
                toggleProduct={toggleProduct}
                removeProduct={removeProduct}
                toggleImportant={toggleImportant}
            />

            {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
                    <button
                        type="button"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                    >
                        Prev
                    </button>

                    {pageNumbers.map(page => (
                        <button
                            key={page}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            style={{
                                fontWeight: page === currentPage ? 700 : 400,
                                textDecoration: page === currentPage ? "underline" : "none"
                            }}
                        >
                            {page}
                        </button>
                    ))}

                    <button
                        type="button"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}
